/**
 * Repeating events (SPEC §4.4).
 *
 * The rule itself has always travelled through the whole stack — `events.rrule`
 * is in the schema, the API round-trips it, the client stores it — and nothing
 * has ever expanded it. A weekly event showed once, at its `startsAt`, wearing a
 * badge that said "repeats", and the reminder job skipped recurring events
 * outright. This is the piece that was missing.
 *
 * It lives in `shared` because both sides have to agree exactly. The client
 * decides which days a dot appears on; the server decides which occurrence to
 * remind about. Two implementations of "every second Tuesday" would drift, and
 * the failure would be a notification for an event the calendar never drew.
 *
 * **A deliberate subset of RFC 5545.** Daily, weekly with a set of weekdays, and
 * monthly on the same date, each with an interval and an optional end. That
 * covers "каждый день", "по будням", "каждую неделю", "раз в месяц" and a
 * hand-picked set of weekdays, which is what a shared calendar for two people
 * is actually asked for. Anything else parses to `null` and is treated as a
 * one-off rather than guessed at — and because the rule is stored as its own
 * RFC 5545 string, a fuller implementation later reads back what this one wrote.
 */

/** Two-letter weekday codes, in RFC 5545's spelling. */
export const WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

export type Weekday = (typeof WEEKDAYS)[number];

export type Frequency = "daily" | "weekly" | "monthly";

export interface Recurrence {
  freq: Frequency;
  /** Every `interval` days / weeks / months. At least 1. */
  interval: number;
  /**
   * Which weekdays, for a weekly rule. Empty means "the weekday the event
   * starts on", which is what a bare `FREQ=WEEKLY` means in the RFC.
   */
  byWeekday: Weekday[];
  /** Inclusive last day the rule may produce, or null for open-ended. */
  until: Date | null;
  /** How many occurrences in total, counting the first, or null. */
  count: number | null;
  /**
   * Days the rule produces but that are not happening.
   *
   * «По будням» is right until the Friday somebody is away, and without this
   * the only way to remove one day is to delete the whole series. RFC 5545
   * calls these EXDATE.
   *
   * **Matched by calendar day, not by exact instant**, which is a deliberate
   * departure from the letter of the RFC. A person cancels a *day*; if the
   * stand-up later moves from 10:00 to 11:00, every occurrence moves with it
   * and an exception recorded against the old instant would silently come back
   * to life. Our subset never produces two occurrences on one day, so a day is
   * unambiguous.
   */
  exceptions: Date[];
}

/** `Date#getDay` is Sunday-first; the RFC's list is Monday-first. */
const WEEKDAY_BY_JS_DAY: readonly Weekday[] = [
  "SU",
  "MO",
  "TU",
  "WE",
  "TH",
  "FR",
  "SA",
];

export function weekdayOf(date: Date): Weekday {
  return WEEKDAY_BY_JS_DAY[date.getDay()]!;
}

const FREQ_BY_NAME: Record<string, Frequency> = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
};

const NAME_BY_FREQ: Record<Frequency, string> = {
  daily: "DAILY",
  weekly: "WEEKLY",
  monthly: "MONTHLY",
};

/**
 * Read an `UNTIL` value.
 *
 * The RFC writes it as `20260831T235959Z` or as a bare `20260831`. Both appear
 * in the wild; a value we cannot read makes the whole rule unreadable rather
 * than silently becoming open-ended, because an open-ended rule repeats forever
 * and that is the wrong way to be wrong.
 */
function parseUntil(raw: string): Date | null {
  const match = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(raw);
  if (!match) return null;

  const [, y, mo, d, h = "23", mi = "59", s = "59"] = match;
  const at = new Date(
    Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s)),
  );

  return Number.isNaN(at.getTime()) ? null : at;
}

/** `YYYYMMDD` in local time — the day, which is what an exception names. */
function formatDay(at: Date): string {
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  return `${pad(at.getFullYear(), 4)}${pad(at.getMonth() + 1)}${pad(at.getDate())}`;
}

/** Two dates on the same local calendar day. */
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Read one EXDATE value as the day it names.
 *
 * Both spellings appear: a bare `20260828` and a full `20260828T100000Z`. The
 * time is read and then only its day is kept — see `exceptions` above for why.
 * A UTC instant is turned into a local day first, so an exception written by
 * one device is the same day on the other.
 */
function parseDay(raw: string): Date | null {
  const match = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z?))?$/.exec(raw);
  if (!match) return null;

  const [, y, mo, d, h, mi, sec, zulu] = match;

  if (h === undefined) {
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }

  const at = zulu
    ? new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(sec)))
    : new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(sec));

  return Number.isNaN(at.getTime())
    ? null
    : new Date(at.getFullYear(), at.getMonth(), at.getDate());
}

function formatUntil(at: Date): string {
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  return (
    `${pad(at.getUTCFullYear(), 4)}${pad(at.getUTCMonth() + 1)}${pad(at.getUTCDate())}` +
    `T${pad(at.getUTCHours())}${pad(at.getUTCMinutes())}${pad(at.getUTCSeconds())}Z`
  );
}

/**
 * Read a rule, or answer `null` for one outside the subset.
 *
 * `null` means "treat this as a one-off", which is exactly what the app did
 * before this file existed — so an unsupported rule degrades to the old
 * behaviour instead of to a crash or to a wrong series.
 */
export function parseRecurrence(rrule: string | null | undefined): Recurrence | null {
  if (!rrule) return null;

  const parts = new Map<string, string>();
  for (const chunk of rrule.replace(/^RRULE:/i, "").split(";")) {
    if (!chunk) continue;
    const at = chunk.indexOf("=");
    if (at < 0) return null;
    parts.set(chunk.slice(0, at).trim().toUpperCase(), chunk.slice(at + 1).trim());
  }

  const freq = FREQ_BY_NAME[(parts.get("FREQ") ?? "").toUpperCase()];
  if (!freq) return null;

  const intervalRaw = parts.get("INTERVAL");
  const interval = intervalRaw === undefined ? 1 : Number(intervalRaw);
  if (!Number.isInteger(interval) || interval < 1) return null;

  let byWeekday: Weekday[] = [];
  const byDayRaw = parts.get("BYDAY");
  if (byDayRaw !== undefined) {
    // Only plain weekday codes. `2MO` ("the second Monday") is a different
    // feature, and answering `null` says so instead of quietly dropping the 2.
    const codes = byDayRaw
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);

    if (codes.some((code) => !WEEKDAYS.includes(code as Weekday))) return null;

    // A weekday list on a daily or monthly rule means something this subset
    // does not implement, so it is not ours to interpret.
    if (freq !== "weekly") return null;

    byWeekday = [...new Set(codes as Weekday[])];
  }

  let until: Date | null = null;
  const untilRaw = parts.get("UNTIL");
  if (untilRaw !== undefined) {
    until = parseUntil(untilRaw);
    if (!until) return null;
  }

  let count: number | null = null;
  const countRaw = parts.get("COUNT");
  if (countRaw !== undefined) {
    count = Number(countRaw);
    if (!Number.isInteger(count) || count < 1) return null;
  }

  // The RFC forbids both at once, and honouring one while ignoring the other
  // would make two clients disagree about where the series stops.
  if (until && count) return null;

  const exceptions: Date[] = [];
  const exdateRaw = parts.get("EXDATE");
  if (exdateRaw !== undefined) {
    for (const value of exdateRaw.split(",").map((one) => one.trim()).filter(Boolean)) {
      const day = parseDay(value);
      // An unreadable exception is not a day we may quietly reinstate: somebody
      // said this one is not happening, and guessing wrong shows an event that
      // was cancelled.
      if (!day) return null;
      exceptions.push(day);
    }
  }

  return { freq, interval, byWeekday, until, count, exceptions };
}

/** Write a rule back out. `parseRecurrence(formatRecurrence(r))` round-trips. */
export function formatRecurrence(rule: Recurrence): string {
  const parts = [`FREQ=${NAME_BY_FREQ[rule.freq]}`];

  if (rule.interval > 1) parts.push(`INTERVAL=${rule.interval}`);
  if (rule.freq === "weekly" && rule.byWeekday.length > 0) {
    // Sorted Monday-first so the same set always writes the same string —
    // otherwise an unchanged rule looks edited every time it is saved.
    const ordered = WEEKDAYS.filter((day) => rule.byWeekday.includes(day));
    parts.push(`BYDAY=${ordered.join(",")}`);
  }
  if (rule.until) parts.push(`UNTIL=${formatUntil(rule.until)}`);
  if (rule.count) parts.push(`COUNT=${rule.count}`);
  if (rule.exceptions.length > 0) {
    // Sorted and de-duplicated so the same set always writes the same string;
    // otherwise an untouched rule looks edited every time it is saved and the
    // sync layer ships a revision for a change nobody made.
    const days = [...new Set(rule.exceptions.map(formatDay))].sort();
    parts.push(`EXDATE=${days.join(",")}`);
  }

  return parts.join(";");
}

/* ------------------------------------------------------------------ expand */

export interface Occurrence {
  /** When this one starts. The first occurrence is the event's own `startsAt`. */
  startsAt: Date;
  /** Same length as the original, or null when the original had no end. */
  endsAt: Date | null;
  /** 0 for the event itself, 1 for the next, and so on. */
  index: number;
}

export interface Repeatable {
  startsAt: Date;
  endsAt?: Date | null;
  rrule?: string | null;
}

/** Move a date by whole calendar days, keeping the wall-clock time. */
function addDays(from: Date, days: number): Date {
  const out = new Date(from);
  out.setDate(out.getDate() + days);
  return out;
}

/**
 * Move by whole months, keeping the day of the month.
 *
 * A rule anchored to the 31st has no 31 September. `setMonth` would roll it into
 * October, so an event would appear on a date the rule never named; the RFC
 * skips such months, and so do we.
 */
function addMonthsExact(from: Date, months: number): Date | null {
  const out = new Date(from);
  const targetMonth = out.getMonth() + months;
  out.setDate(1);
  out.setMonth(targetMonth);
  out.setDate(from.getDate());

  // If the day rolled over, `from.getDate()` did not exist in that month.
  return out.getDate() === from.getDate() ? out : null;
}

/**
 * A ceiling on how many occurrences one call may return.
 *
 * Not a guard against walking from the series' beginning — the expansion jumps
 * straight to the window rather than counting its way there, because a daily
 * stand-up created three years ago is a thousand steps away from today and a
 * guard would have cut the series off before it ever reached the screen.
 */
const MAX_OCCURRENCES = 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Every occurrence that overlaps `[from, to)`.
 *
 * An event that *started* before the window but has not finished is included: a
 * meeting running from 09:00 to 11:00 is still happening at 10:00, and a day
 * view that dropped it would be lying.
 *
 * A non-repeating event yields itself when it overlaps, so callers do not need
 * two code paths — which is also how the old "shows once at `startsAt`"
 * behaviour is preserved for rules this subset does not understand.
 */
export function expandOccurrences(event: Repeatable, from: Date, to: Date): Occurrence[] {
  const length =
    event.endsAt && event.endsAt > event.startsAt
      ? event.endsAt.getTime() - event.startsAt.getTime()
      : 0;

  const endOf = (start: Date): Date | null =>
    event.endsAt ? new Date(start.getTime() + length) : null;

  const overlaps = (start: Date): boolean => {
    const finish = start.getTime() + length;
    // `finish >= from` rather than `>`: a zero-length event exactly on the
    // boundary belongs to the window that starts there.
    return start < to && finish >= from.getTime();
  };

  const rule = parseRecurrence(event.rrule);

  if (!rule) {
    return overlaps(event.startsAt)
      ? [{ startsAt: event.startsAt, endsAt: endOf(event.startsAt), index: 0 }]
      : [];
  }

  const out: Occurrence[] = [];
  const limit = rule.count ?? Infinity;

  /**
   * How far ahead the window is, in whole steps, minus one for safety.
   *
   * Deliberately approximate, and deliberately low: landing a step early costs
   * one wasted iteration, landing a step late silently drops an occurrence.
   *
   * `count` disables it: the index of an occurrence has to be exact for COUNT
   * to stop in the right place, and a bounded series is short enough to walk.
   */
  const skipSteps = (stepMs: number): number => {
    if (rule.count) return 0;
    const ahead = from.getTime() - length - event.startsAt.getTime();
    if (ahead <= 0) return 0;
    return Math.max(0, Math.floor(ahead / stepMs) - 1);
  };

  const take = (start: Date, index: number): boolean => {
    if (rule.until && start > rule.until) return false;
    if (index >= limit) return false;
    if (start.getTime() >= to.getTime()) return false;

    /*
     * An excepted day is skipped but still counted.
     *
     * It occupies its place in the series: `COUNT=5` with one day cancelled
     * means four happen, not that the series reaches further to make up the
     * number. That is what the RFC says and it is also what a person means by
     * "cancel that one".
     */
    if (rule.exceptions.some((day) => sameDay(day, start))) return true;

    if (overlaps(start)) {
      out.push({ startsAt: start, endsAt: endOf(start), index });
    }
    return true;
  };

  if (rule.freq === "daily") {
    const first = skipSteps(DAY_MS * rule.interval);
    for (let i = first, guard = 0; guard < MAX_OCCURRENCES; i += 1, guard += 1) {
      if (!take(addDays(event.startsAt, i * rule.interval), i)) break;
    }
    return out;
  }

  if (rule.freq === "monthly") {
    // Months are not a fixed number of milliseconds, so the jump is counted in
    // months rather than divided out of a duration.
    const monthsAhead =
      (from.getFullYear() - event.startsAt.getFullYear()) * 12 +
      (from.getMonth() - event.startsAt.getMonth());
    const first = rule.count ? 0 : Math.max(0, Math.floor(monthsAhead / rule.interval) - 1);

    for (let i = first, guard = 0; guard < MAX_OCCURRENCES; i += 1, guard += 1) {
      const start = addMonthsExact(event.startsAt, i * rule.interval);
      // A month the rule's day does not exist in is skipped, not shifted — and
      // it must not end the series either, or a rule on the 31st would stop in
      // February.
      if (!start) continue;
      if (!take(start, i)) break;
    }
    return out;
  }

  /*
   * Weekly.
   *
   * The interval counts *weeks*, not occurrences, so a rule that fires three
   * times a week every second week has to walk week by week and emit the days
   * inside each. The week is anchored to the Monday on or before the event's
   * own start, so "every second week" means the same weeks whichever day inside
   * one you happen to be looking from.
   */
  const days = rule.byWeekday.length > 0 ? rule.byWeekday : [weekdayOf(event.startsAt)];
  const offsetsInWeek = WEEKDAYS.map((day, i) => (days.includes(day) ? i : -1)).filter(
    (i) => i >= 0,
  );

  const startWeekday = WEEKDAYS.indexOf(weekdayOf(event.startsAt));
  const anchor = addDays(event.startsAt, -startWeekday);

  /*
   * The index has to be arithmetic, not a running counter, because the walk
   * starts at the window rather than at the beginning of the series. Days in
   * the first week that fall before the event itself are not occurrences, so
   * they are subtracted once, here.
   */
  const perWeek = offsetsInWeek.length;
  const missedInFirstWeek = offsetsInWeek.filter(
    (offset) => addDays(anchor, offset) < event.startsAt,
  ).length;

  const firstWeek = rule.count
    ? 0
    : Math.max(
        0,
        Math.floor(
          Math.max(0, from.getTime() - length - anchor.getTime()) /
            (DAY_MS * 7 * rule.interval),
        ) - 1,
      );

  for (let week = firstWeek, guard = 0; guard < MAX_OCCURRENCES; week += 1, guard += 1) {
    const weekStart = addDays(anchor, week * rule.interval * 7);

    // The whole week is past the window: no later week can help.
    if (weekStart.getTime() >= to.getTime()) break;

    let stopped = false;

    for (const [position, offset] of offsetsInWeek.entries()) {
      const start = addDays(weekStart, offset);
      if (start < event.startsAt) continue;

      if (!take(start, week * perWeek + position - missedInFirstWeek)) {
        stopped = true;
        break;
      }
    }

    if (stopped) break;
  }

  return out;
}

/** Whether an event repeats in a way this subset understands. */
export function repeats(rrule: string | null | undefined): boolean {
  return parseRecurrence(rrule) !== null;
}
